"use client";

import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from "react";

// 입력 칸 (DS TextField). 라벨이 칸을 감싸서 접근 가능한 이름이 된다.
// 라벨 줄 오른쪽에 글자 수, 칸 아래에 도움말이나 오류를 둔다. 글자 수는 부르는 쪽이 @muse/contracts로 센다 (W-R03)

/** 입력 칸 하나의 DS 클래스. 라벨 없이 칸만 그릴 때 쓴다 */
export function controlClass({
  locked = false,
  className,
}: {
  locked?: boolean;
  className?: string;
} = {}): string {
  return ["st-control", locked ? "st-control--locked" : "", className ?? ""]
    .filter((part) => part !== "")
    .join(" ");
}

interface FieldSlots {
  label: ReactNode;
  /** 칸 아래 도움말 (`st-hint`) */
  hint?: ReactNode;
  /** 라벨 줄 오른쪽 글자 수·개수 (예: "742 / 1,000자") */
  count?: ReactNode;
  /** 글자 수가 제한을 넘었다 (`st-count--over`) */
  countOver?: boolean;
  /** 칸 아래 오류. 있으면 aria-invalid가 켜진다 */
  error?: ReactNode;
  /** 값이 보이지만 고칠 수 없는 칸 (readOnly) */
  locked?: boolean;
}

function useSlotIds(hint: ReactNode, error: ReactNode, describedBy: string | undefined) {
  const id = useId();
  const hintId = hint !== undefined && hint !== null ? `${id}-hint` : undefined;
  const errorId =
    error !== undefined && error !== null && error !== false ? `${id}-error` : undefined;
  const ids = [describedBy, hintId, errorId].filter((value) => value !== undefined).join(" ");
  return { hintId, errorId, describedBy: ids === "" ? undefined : ids };
}

function LabelRow({ label, count, countOver }: Pick<FieldSlots, "label" | "count" | "countOver">) {
  return (
    <span className="st-label">
      <span>{label}</span>
      {count !== undefined && count !== null && (
        <span className={countOver === true ? "st-count st-count--over" : "st-count"}>{count}</span>
      )}
    </span>
  );
}

function SlotHints({
  hint,
  error,
  hintId,
  errorId,
}: Pick<FieldSlots, "hint" | "error"> & { hintId?: string; errorId?: string }) {
  return (
    <>
      {hintId !== undefined && (
        <span id={hintId} className="st-hint block">
          {hint}
        </span>
      )}
      {errorId !== undefined && (
        <span id={errorId} className="st-hint st-hint--error block">
          {error}
        </span>
      )}
    </>
  );
}

interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "children">,
    FieldSlots {}

export function TextField({
  label,
  hint,
  count,
  countOver,
  error,
  locked = false,
  className,
  readOnly,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: TextFieldProps) {
  const ids = useSlotIds(hint, error, ariaDescribedBy);
  return (
    <label className={`st-field ${className ?? ""}`}>
      <LabelRow label={label} count={count} countOver={countOver} />
      <input
        className={controlClass({ locked })}
        readOnly={locked || readOnly}
        aria-invalid={ids.errorId !== undefined ? true : ariaInvalid}
        aria-describedby={ids.describedBy}
        {...rest}
      />
      <SlotHints hint={hint} error={error} hintId={ids.hintId} errorId={ids.errorId} />
    </label>
  );
}

interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "children">,
    FieldSlots {}

export function TextAreaField({
  label,
  hint,
  count,
  countOver,
  error,
  locked = false,
  className,
  rows = 3,
  readOnly,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: TextAreaFieldProps) {
  const ids = useSlotIds(hint, error, ariaDescribedBy);
  return (
    <label className={`st-field ${className ?? ""}`}>
      <LabelRow label={label} count={count} countOver={countOver} />
      <textarea
        className={controlClass({ locked })}
        rows={rows}
        readOnly={locked || readOnly}
        aria-invalid={ids.errorId !== undefined ? true : ariaInvalid}
        aria-describedby={ids.describedBy}
        {...rest}
      />
      <SlotHints hint={hint} error={error} hintId={ids.hintId} errorId={ids.errorId} />
    </label>
  );
}

interface SelectFieldProps
  extends SelectHTMLAttributes<HTMLSelectElement>,
    Omit<FieldSlots, "count" | "countOver"> {
  children: ReactNode;
}

export function SelectField({
  label,
  hint,
  error,
  locked = false,
  className,
  children,
  disabled,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: SelectFieldProps) {
  const ids = useSlotIds(hint, error, ariaDescribedBy);
  return (
    <label className={`st-field ${className ?? ""}`}>
      <LabelRow label={label} />
      {/* 선택 칸에는 readOnly가 없어 잠금은 disabled로 막는다 */}
      <select
        className={controlClass({ locked })}
        disabled={locked || disabled}
        aria-invalid={ids.errorId !== undefined ? true : ariaInvalid}
        aria-describedby={ids.describedBy}
        {...rest}
      >
        {children}
      </select>
      <SlotHints hint={hint} error={error} hintId={ids.hintId} errorId={ids.errorId} />
    </label>
  );
}

interface CheckFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "children"> {
  label: ReactNode;
  /** 라벨 옆 작은 글 (예: "사람만 켤 수 있습니다") */
  note?: ReactNode;
}

/** 체크 칸 (DS `st-check`) */
export function CheckField({ label, note, className, ...rest }: CheckFieldProps) {
  return (
    <label className={`st-check ${className ?? ""}`}>
      <input type="checkbox" {...rest} />
      {label}
      {note !== undefined && <span className="st-subtle">{note}</span>}
    </label>
  );
}
